import React from 'react'
import { Permission, usePermissions } from '../hooks/usePermissions'

interface PermissionGateProps {
  children: React.ReactNode
  
  // Permission requirements
  permission?: Permission
  permissions?: Permission[]
  requireAll?: boolean // If true, user must have all permissions. If false, user needs any permission.
  
  // Role-based access
  adminOnly?: boolean
  superAdminOnly?: boolean
  
  // Project context
  projectId?: string
  
  // Fallback content
  fallback?: React.ReactNode
  
  // Loading state
  showLoadingFallback?: boolean
  loadingComponent?: React.ReactNode
  
  // Error handling
  showErrorFallback?: boolean
  errorComponent?: React.ReactNode
  
  // Additional checks
  customCheck?: (permissionContext: {
    hasPermission: (permission: Permission) => boolean
    hasAllPermissions: (permissions: Permission[]) => boolean
    hasAnyPermission: (permissions: Permission[]) => boolean
    isSuperAdmin: boolean
    isAdmin: boolean
    permissions: any
  }) => boolean
}

/**
 * PermissionGate component for conditional rendering based on user permissions
 * 
 * Usage examples:
 * 
 * // Single permission check
 * <PermissionGate permission={Permission.MANAGE_USERS}>
 *   <AdminButton />
 * </PermissionGate>
 * 
 * // Multiple permissions (any)
 * <PermissionGate permissions={[Permission.EDIT_ISSUE, Permission.DELETE_ISSUE]}>
 *   <IssueActions />
 * </PermissionGate>
 * 
 * // Multiple permissions (all required)
 * <PermissionGate permissions={[Permission.MANAGE_PROJECT, Permission.DELETE_PROJECT]} requireAll>
 *   <DangerousProjectActions />
 * </PermissionGate>
 * 
 * // Role-based access
 * <PermissionGate adminOnly>
 *   <AdminPanel />
 * </PermissionGate>
 * 
 * // Project-specific permissions
 * <PermissionGate permission={Permission.MANAGE_PROJECT_MEMBERS} projectId={project.id}>
 *   <MemberManagement />
 * </PermissionGate>
 * 
 * // With fallback content
 * <PermissionGate permission={Permission.VIEW_AUDIT_LOGS} fallback={<AccessDenied />}>
 *   <AuditLogViewer />
 * </PermissionGate>
 * 
 * // Custom permission check
 * <PermissionGate customCheck={({ isAdmin, hasPermission }) => 
 *   isAdmin || hasPermission(Permission.SPECIAL_ACCESS)
 * }>
 *   <SpecialFeature />
 * </PermissionGate>
 */
const PermissionGate: React.FC<PermissionGateProps> = ({
  children,
  permission,
  permissions,
  requireAll = false,
  adminOnly = false,
  superAdminOnly = false,
  projectId,
  fallback = null,
  showLoadingFallback = true,
  loadingComponent,
  showErrorFallback = false,
  errorComponent,
  customCheck
}) => {
  const {
    loading,
    error,
    hasPermission,
    hasAllPermissions,
    hasAnyPermission,
    isSuperAdmin,
    isAdmin,
    permissions: userPermissions
  } = usePermissions(projectId)

  // Show loading state
  if (loading && showLoadingFallback) {
    if (loadingComponent) {
      return <>{loadingComponent}</>
    }
    
    return (
      <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  // Show error state
  if (error && showErrorFallback) {
    if (errorComponent) {
      return <>{errorComponent}</>
    }
    
    return (
      <div className="text-red-600 text-sm p-2">
        載入權限失敗: {error}
      </div>
    )
  }

  // Super admin check (highest priority)
  if (superAdminOnly) {
    return isSuperAdmin ? <>{children}</> : <>{fallback}</>
  }

  // Admin check
  if (adminOnly) {
    return isAdmin ? <>{children}</> : <>{fallback}</>
  }

  // Custom permission check
  if (customCheck) {
    const permissionContext = {
      hasPermission,
      hasAllPermissions,
      hasAnyPermission,
      isSuperAdmin,
      isAdmin,
      permissions: userPermissions
    }
    
    return customCheck(permissionContext) ? <>{children}</> : <>{fallback}</>
  }

  // Single permission check
  if (permission) {
    return hasPermission(permission) ? <>{children}</> : <>{fallback}</>
  }

  // Multiple permissions check
  if (permissions && permissions.length > 0) {
    const hasRequiredPermissions = requireAll 
      ? hasAllPermissions(permissions)
      : hasAnyPermission(permissions)
    
    return hasRequiredPermissions ? <>{children}</> : <>{fallback}</>
  }

  // If no permission checks are specified, render children by default
  return <>{children}</>
}

export default PermissionGate

// Convenience components for common use cases

/**
 * AdminOnly component - only renders for admin users
 */
export const AdminOnly: React.FC<{
  children: React.ReactNode
  fallback?: React.ReactNode
}> = ({ children, fallback }) => (
  <PermissionGate adminOnly fallback={fallback}>
    {children}
  </PermissionGate>
)

/**
 * SuperAdminOnly component - only renders for super admin users
 */
export const SuperAdminOnly: React.FC<{
  children: React.ReactNode
  fallback?: React.ReactNode
}> = ({ children, fallback }) => (
  <PermissionGate superAdminOnly fallback={fallback}>
    {children}
  </PermissionGate>
)

/**
 * WithPermission component - renders only if user has the specified permission
 */
export const WithPermission: React.FC<{
  permission: Permission
  children: React.ReactNode
  fallback?: React.ReactNode
  projectId?: string
}> = ({ permission, children, fallback, projectId }) => (
  <PermissionGate permission={permission} fallback={fallback} projectId={projectId}>
    {children}
  </PermissionGate>
)

/**
 * WithAnyPermission component - renders if user has any of the specified permissions
 */
export const WithAnyPermission: React.FC<{
  permissions: Permission[]
  children: React.ReactNode
  fallback?: React.ReactNode
  projectId?: string
}> = ({ permissions, children, fallback, projectId }) => (
  <PermissionGate permissions={permissions} requireAll={false} fallback={fallback} projectId={projectId}>
    {children}
  </PermissionGate>
)

/**
 * WithAllPermissions component - renders only if user has all specified permissions
 */
export const WithAllPermissions: React.FC<{
  permissions: Permission[]
  children: React.ReactNode
  fallback?: React.ReactNode
  projectId?: string
}> = ({ permissions, children, fallback, projectId }) => (
  <PermissionGate permissions={permissions} requireAll={true} fallback={fallback} projectId={projectId}>
    {children}
  </PermissionGate>
)